import { useState } from "react";
import ChartFactory from "src/charts/ChartFactory";
import WidgetPlaceholder from "./WidgetPlaceholder";
import type { QuadrantPractice, DrilldownRow } from "src/charts/types";
import { getTierStyle } from "src/charts/types";

type QuadrantScatterWidgetProps = {
  isLoading: boolean;
  title: string;
  description?: string | null;
  data: unknown;
  spec?: Record<string, unknown>;
  widgetId?: string | number;
};

function fmt(val: number | string | undefined): string {
  if (val === undefined || val === null || val === "") return "—";
  const n = Number(val);
  return Number.isFinite(n) ? n.toLocaleString() : String(val);
}

function StatCard({ label, value, accent }: { label: string; value: string; accent?: string }) {
  return (
    <div
      style={{
        flex: "1 1 100px",
        minWidth: 90,
        padding: "8px 12px",
        borderRadius: 8,
        background: "var(--bs-tertiary-bg, #f8fafc)",
        border: "1px solid var(--bs-border-color)",
      }}
    >
      <div style={{ fontSize: 10, color: "var(--bs-secondary-color)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 2 }}>
        {label}
      </div>
      <div style={{ fontSize: 15, fontWeight: 700, color: accent ?? "var(--bs-body-color)" }}>
        {value}
      </div>
    </div>
  );
}

function TierBadge({ tier }: { tier?: string }) {
  const s = getTierStyle(tier);
  if (!tier) return null;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "2px 8px",
        borderRadius: 20,
        fontSize: 11,
        fontWeight: 600,
        background: s.bg,
        color: s.text,
        border: `1px solid ${s.border}`,
        whiteSpace: "nowrap",
      }}
    >
      <span style={{ color: s.dot, fontSize: 8 }}>●</span>
      {tier}
    </span>
  );
}

function DrilldownPanel({
  practice,
  onClose,
}: {
  practice: QuadrantPractice;
  onClose: () => void;
}) {
  const style = getTierStyle(practice.tier);
  const rows: DrilldownRow[] = practice.drilldown?.rows ?? [];
  const count = practice.drilldown?.count ?? rows.length;

  return (
    <div
      className="flex-shrink-0"
      style={{
        borderTop: `3px solid ${style.dot}`,
        maxHeight: "48%",
        overflowY: "auto",
        background: "var(--bs-card-bg)",
      }}
    >
      {/* Header bar */}
      <div
        className="d-flex align-items-center gap-2 px-3 py-2"
        style={{
          background: style.headerBg,
          borderBottom: `1px solid ${style.border}`,
          position: "sticky",
          top: 0,
          zIndex: 2,
        }}
      >
        <span
          style={{
            width: 10,
            height: 10,
            borderRadius: "50%",
            background: style.dot,
            flexShrink: 0,
            display: "inline-block",
          }}
        />
        <span style={{ fontSize: 13, fontWeight: 700, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: style.text }}>
          {practice.practice ?? "Practice"}
        </span>
        <TierBadge tier={practice.tier} />
        <span
          style={{
            fontSize: 11,
            padding: "1px 8px",
            borderRadius: 20,
            background: "var(--bs-secondary-bg, #e2e8f0)",
            color: "var(--bs-secondary-color)",
            border: "1px solid var(--bs-border-color)",
            whiteSpace: "nowrap",
            flexShrink: 0,
          }}
        >
          {count} prescriber{count !== 1 ? "s" : ""}
        </span>
        <button
          className="btn-close"
          style={{ fontSize: 10, flexShrink: 0 }}
          onClick={onClose}
          aria-label="Close drilldown"
        />
      </div>

      {/* Stat summary */}
      <div className="d-flex flex-wrap gap-2 px-3 py-2" style={{ borderBottom: "1px solid var(--bs-border-color)" }}>
        <StatCard label="Total Cases" value={fmt(practice.pts)} />
        <StatCard label="PA Cases" value={fmt(practice.pa_cases)} />
        <StatCard label="PA Approved" value={fmt(practice.pa_approved_cases)} />
        <StatCard label="PA Success" value={practice.pa_percentage ?? "—"} accent={style.dot} />
        <StatCard label="Abandon Rate" value={practice.abandon_percentage ?? "—"} />
      </div>

      {/* Prescribers table */}
      {rows.length === 0 ? (
        <div className="px-3 py-3 text-center text-muted" style={{ fontSize: 13 }}>
          No prescriber data available.
        </div>
      ) : (
        <table
          className="table table-hover mb-0"
          style={{ fontSize: 12, tableLayout: "fixed" }}
        >
          <colgroup>
            <col style={{ width: "30%" }} />
            <col style={{ width: "13%" }} />
            <col style={{ width: "13%" }} />
            <col style={{ width: "12%" }} />
            <col style={{ width: "12%" }} />
            <col style={{ width: "20%" }} />
          </colgroup>
          <thead style={{ position: "sticky", top: 0, zIndex: 1, background: "var(--bs-tertiary-bg, #f8fafc)" }}>
            <tr>
              <th className="text-center" style={{ fontWeight: 600, fontSize: 11, color: "var(--bs-secondary-color)", textTransform: "uppercase", letterSpacing: "0.04em" }}>Prescriber</th>
              <th className="text-center" style={{ fontWeight: 600, fontSize: 11, color: "var(--bs-secondary-color)", textTransform: "uppercase", letterSpacing: "0.04em" }}>Total Cases</th>
              <th className="text-center" style={{ fontWeight: 600, fontSize: 11, color: "var(--bs-secondary-color)", textTransform: "uppercase", letterSpacing: "0.04em" }}>PA Cases</th>
              <th className="text-center" style={{ fontWeight: 600, fontSize: 11, color: "var(--bs-secondary-color)", textTransform: "uppercase", letterSpacing: "0.04em" }}>PA%</th>
              <th className="text-center" style={{ fontWeight: 600, fontSize: 11, color: "var(--bs-secondary-color)", textTransform: "uppercase", letterSpacing: "0.04em" }}>Abandon%</th>
              <th className="text-center" style={{ fontWeight: 600, fontSize: 11, color: "var(--bs-secondary-color)", textTransform: "uppercase", letterSpacing: "0.04em" }}>Tier</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => {
              const rs = getTierStyle(row.tier);
              return (
                <tr
                  key={row.doctor_id ?? i}
                  style={{
                    verticalAlign: "middle",
                    background: rs.bg,
                    borderLeft: `3px solid ${rs.dot}`,
                  }}
                >
                  <td
                    className="text-center fw-semibold"
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      maxWidth: 0,
                      color: rs.text,
                    }}
                    title={row.prescriber}
                  >
                    {row.prescriber ?? "—"}
                  </td>
                  <td className="text-center tabular-nums" style={{ color: "var(--bs-body-color)" }}>{fmt(row.pts)}</td>
                  <td className="text-center tabular-nums" style={{ color: "var(--bs-body-color)" }}>{fmt(row.pa_cases)}</td>
                  <td className="text-center tabular-nums" style={{ color: "var(--bs-body-color)" }}>{row.pa_percentage ?? "—"}</td>
                  <td className="text-center tabular-nums" style={{ color: "var(--bs-body-color)" }}>{row.abandon_percentage ?? "—"}</td>
                  <td className="text-center">
                    <TierBadge tier={row.tier} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default function QuadrantScatterWidget({
  isLoading,
  title,
  description,
  data,
  widgetId,
}: QuadrantScatterWidgetProps) {
  const [selectedPractice, setSelectedPractice] = useState<QuadrantPractice | null>(null);

  if (isLoading || data === undefined || data === null) {
    return <WidgetPlaceholder height={320} />;
  }

  const handlePointClick = (point: unknown) => {
    const practice = point as QuadrantPractice | null;
    if (!practice) {
      setSelectedPractice(null);
      return;
    }
    setSelectedPractice((prev) =>
      prev?.practice_id === practice.practice_id && prev?.practice === practice.practice
        ? null
        : practice,
    );
  };

  return (
    <div className="d-flex flex-column h-100" style={{ minHeight: 0 }}>
      <div className="flex-fill" style={{ minHeight: 0 }}>
        <ChartFactory
          chartType="quadrant_scatter"
          chartId={`${widgetId ?? `${title?.replace(/\s+/g, "-") || "chart"}-quadrant-scatter`}`}
          title={title}
          description={description}
          data={
            selectedPractice
              ? {
                  ...(Array.isArray(data) ? { practices: data } : (data as any) ?? {}),
                  _selectedId: selectedPractice.practice_id ?? selectedPractice.practice,
                }
              : data
          }
          fillHeight
          onPointClick={handlePointClick}
        />
      </div>
      {selectedPractice && (
        <DrilldownPanel
          practice={selectedPractice}
          onClose={() => setSelectedPractice(null)}
        />
      )}
    </div>
  );
}
